<template>
  <div class="nexus-container">
    <v-container fluid fill-height>
      <!-- Back Button -->
      <v-btn icon class="back-button" @click="confirmBackNavigation">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>

      <!-- Header Section -->
      <v-row class="pa-7" justify="center">
        <h1 class="nexus-title">EDIT RETURNS</h1>
      </v-row>

      <!-- Main Form Section -->
      <v-row class="fill-height pa-0 ma-0" justify="center" align="start">
        <v-col cols="12" md="10" class="pa-0">
          <v-card elevation="0" class="nexus-card">
            <v-card-text class="pa-8">
              <!-- Loading Overlay -->
              <v-overlay :value="loading">
                <v-progress-circular indeterminate size="64"></v-progress-circular>
              </v-overlay>

              <!-- Form -->
              <v-form ref="form" v-model="valid">
                <!-- RETURN DETAILS SECTION -->
                <div class="redesign-section-header">
                  <h2 class="redesign-section-title">RETURN DETAILS</h2>
                </div>

                <!-- Two Column Layout -->
                <v-row class="mt-4">
                  <!-- LEFT COLUMN - Read-only Info -->
                  <v-col cols="12" md="6">
                    <!-- Info Box 1 -->
                    <div class="info-box mb-4">
                      <div class="info-item">
                        <div class="info-label">
                          <v-icon small color="#475569">mdi-identifier</v-icon>
                          Return ID
                        </div>
                        <div class="info-value">#{{ returnData.returnId }}</div>
                      </div>
                      <div class="info-item">
                        <div class="info-label">
                          <v-icon small color="#475569">mdi-calendar</v-icon>
                          Date Received
                        </div>
                        <div class="info-value">{{ formatDate(returnData.dateReceived) }}</div>
                      </div>
                      <div class="info-item">
                        <div class="info-label">
                          <v-icon small color="#475569">mdi-calendar-check</v-icon>
                          Created Date
                        </div>
                        <div class="info-value">{{ returnData.createdDate }}</div>
                      </div>
                    </div>

                    <!-- Info Box 2 -->
                    <div class="info-box">
                      <div class="info-item">
                        <div class="info-label">
                          <v-icon small color="#475569">mdi-office-building</v-icon>
                          Customer
                        </div>
                        <div class="info-value">{{ returnData.customer ? returnData.customer.companyName : 'N/A' }}</div>
                      </div>
                      <div class="info-item">
                        <div class="info-label">
                          <v-icon small color="#475569">mdi-account</v-icon>
                          Contact Person
                        </div>
                        <div class="info-value">{{ returnData.contactPerson || 'N/A' }}</div>
                      </div>
                      <div class="info-item">
                        <div class="info-label">
                          <v-icon small color="#475569">mdi-file-document</v-icon>
                          Invoice Number
                        </div>
                        <div class="info-value">{{ returnData.invoiceNumber || 'N/A' }}</div>
                      </div>
                    </div>
                  </v-col>

                  <!-- RIGHT COLUMN - Editable Fields -->
                  <v-col cols="12" md="6">
                    <!-- Received By -->
                    <div class="field-group mb-4">
                      <div class="field-label">
                        <v-icon small>mdi-account</v-icon>
                        Received By
                      </div>
                      <v-select
                        v-model="returnData.receivedBy"
                        :rules="[rules.required]"
                        :items="receivedByOptions"
                        outlined
                        dense
                        class="nexus-input mt-2"
                        :disabled="!isEditing"
                      ></v-select>
                    </div>

                    <!-- Return Status -->
                    <div class="field-group mb-4">
                      <div class="field-label">
                        <v-icon small>mdi-file-edit</v-icon>
                        Return Status
                      </div>
                      <v-select
                        v-model="returnData.returnStatus"
                        :items="statusOptions"
                        outlined
                        dense
                        class="nexus-input mt-2"
                        @change="fetchAssignedToOptions"
                        :disabled="canEditReturnStatus"
                      ></v-select>
                    </div>

                    <!-- Assigned To -->
                    <div class="field-group mb-4" v-if="returnData.returnStatus !== 'Completed' && returnData.returnStatus !== 'Rejected'">
  <div class="field-label">
    <v-icon small>mdi-account-check</v-icon>
    Assigned To
  </div>
  <v-select
    v-model="returnData.inspection"
    :rules="returnData.returnStatus === 'Rejected' ? [] : [rules.required]"
    :items="assignedToOptions"
    outlined
    dense
    class="nexus-input mt-2"
  ></v-select>
</div>

                    <!-- Credit Note Number -->
                    <div class="field-group">
                      <div class="field-label">
                        <v-icon small>mdi-file-document-outline</v-icon>
                        Credit Note Number
                      </div>
                      <v-text-field
                        v-model="returnData.creditNoteNumber"
                        outlined
                        dense
                        class="nexus-input mt-2"
                        placeholder="Enter credit note number"
                        :disabled="returnData.returnStatus !== 'Pending Credit'"
                      ></v-text-field>
                    </div>
                  </v-col>
                </v-row>

                <!-- Customer and Invoice Details Row (hidden - now in info boxes above) -->
                <v-row class="d-none">
                </v-row>

                <!-- Delivery Date and Created Date (hidden - shown in info boxes) -->
                <v-row class="d-none">
                </v-row>

                <!-- Return Reason Box -->
                <v-row class="mt-4">
                  <v-col cols="12">
                    <div class="reason-box">
                      <div class="reason-label">
                        <v-icon small color="#475569">mdi-comment-text</v-icon>
                        Return Reason
                      </div>
                      <v-textarea
                        v-model="returnData.returnReason"
                        outlined
                        dense
                        rows="3"
                        class="nexus-input mt-2"
                        :disabled="!isEditing"
                      ></v-textarea>
                    </div>
                  </v-col>
                </v-row>

                <!-- Old fields hidden - now shown in new layout above -->
                <v-row class="d-none">
                  <!-- Customer Field -->
                  <v-col cols="12" md="3" class="pa-2">
                      <v-autocomplete
                          v-model="selectedCustomer"
                          :items="customers"
                          item-title="companyName"
                          item-value="docId"
                          label="Customer"
                          outlined
                          rounded
                          dense
                          prepend-inner-icon="mdi-account"
                          class="nexus-input"
                          :rules="[rules.required]"
                          return-object
                          :disabled="!isEditing"
                          
                          
                        ></v-autocomplete>
                  </v-col>

                  <!-- Contact Person -->
                  <v-col cols="12" md="3" class="pa-2">
                      <v-text-field
                          v-model="returnData.contactPerson"
                          :rules="[rules.required]"
                          label="Contact Person"
                          rounded
                          dense
                          outlined
                          prepend-inner-icon="mdi-account-box-outline"
                          class="nexus-input"
                          :disabled="!isEditing"
                          
                          
                        ></v-text-field>
                  </v-col>

                  <!-- Invoice Number -->
                  <v-col cols="12" md="2" class="pa-2">
                      <v-text-field
                          v-model="returnData.invoiceNumber"
                          :rules="[rules.required]"
                          label="Invoice Number"
                          rounded
                          dense
                          outlined
                          prepend-inner-icon="mdi-file-document"
                          class="nexus-input"
                          :disabled="!isEditing"
                          
                          
                        ></v-text-field>
                  </v-col>

                  <!-- Credit Note Number -->
                  <v-col cols="12" md="3" v-if="returnData.returnStatus !== 'Booked' && returnData.returnStatus !== 'Inspection'" class="pa-2">
                      <v-text-field
                          v-model="returnData.creditNoteNumber"
                          :rules="creditNoteNumberRules"
                          outlined
                          rounded
                          dense
                          prepend-inner-icon="mdi-file-document-outline"
                          class="nexus-input"
                        >
                          <template v-slot:label>
                            Credit Note Number
                            <span v-if="returnData.returnStatus === 'Labels and Packaging'" style="color: red;">*</span>
                          </template>
                        </v-text-field>
                  </v-col>
                </v-row>

                <!-- Delivery Date and Created Date -->
                <v-row>
                  <!-- Delivery Date -->
                  <v-col cols="12" md="2" class="pa-2">
                    <v-menu
                      ref="deliveryDateMenu"
                      v-model="deliveryDateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ props }">
                        <v-text-field
                          v-model="deliveryDate"
                          label="Date Received"
                          prepend-inner-icon="mdi-calendar"
                          readonly
                          outlined
                          dense
                          rounded
                          class="nexus-input"
                          v-bind="props"
                          :rules="[rules.required]"
                          :disabled="!isEditing"
                        ></v-text-field>
                      </template>
                      <v-date-picker v-model="deliveryDate" @input="deliveryDateMenu = false" :disabled="!isEditing"></v-date-picker>
                    </v-menu>
                  </v-col>

                  <!-- Created Date -->
                  <v-col cols="12" md="2" class="pa-2">
                      <v-text-field
                          v-model="createdDate"
                          label="Created Date"
                          readonly
                          outlined
                          dense
                          rounded
                          prepend-inner-icon="mdi-calendar-text"
                          class="nexus-input"
                          :disabled="!isEditing"
                          
                          
                        ></v-text-field>
                  </v-col>
                </v-row>

                <!-- Return Reason -->
                <v-row>
                  <v-col cols="12" md="12" class="pa-2">
                      <v-textarea
                          v-model="returnData.returnReason"
                          :rules="[rules.required]"
                          label="Return Reason"
                          rounded
                          dense
                          outlined
                          prepend-inner-icon="mdi-comment-text-outline"
                          class="nexus-input"
                          :disabled="!isEditing"
                          
                          
                        ></v-textarea>
                  </v-col>
                </v-row>
                <!-- End hidden old fields row -->

                <!-- Return Items Fields -->
                <!-- This section is hidden if the current user's returns permission "View_Line_Items_(Labour_and_Stock)" is true -->
                <div v-if="!hideReturnLineItems">
                  <div class="redesign-section-header mt-8">
                    <h2 class="redesign-section-title">RETURN ITEMS</h2>
                    <v-btn
                      @click="downloadAllPhotos"
                      class="nexus-btn"
                    >
                      <v-icon class="mr-2">mdi-download</v-icon>
                      Download All Photos
                    </v-btn>
                  </div>

                  <!-- Item Cards -->
                  <div v-for="(returnItem, index) in returnData.returnItems" :key="index" class="item-card mt-4">
                    <!-- Item Header -->
                    <div class="item-header">
                      <div class="item-number">Item {{ index + 1 }} of {{ returnData.returnItems.length }}</div>
                      <v-chip
                        :class="returnItem.status === 'Approved' ? 'status-approved' : 'status-rejected'"
                        label
                        small
                      >
                        <v-icon class="mr-1" size="small">{{ returnItem.status === 'Approved' ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
                        {{ returnItem.status }}
                      </v-chip>
                    </div>

                    <!-- Item Details Grid -->
                    <v-row class="item-details">
                      <!-- QTY -->
                      <v-col cols="6" md="1">
                        <div class="detail-label">Quantity</div>
                        <v-text-field
                          v-model="returnItem.qty"
                          :rules="[rules.required, rules.numeric]"
                          outlined
                          dense
                          hide-details
                          class="nexus-input"
                          :disabled="!isEditing"
                        ></v-text-field>
                      </v-col>

                      <!-- Code -->
                      <v-col cols="6" md="3">
                        <div class="detail-label">Code</div>
                        <v-text-field
                          v-model="returnItem.code"
                          :rules="[rules.required]"
                          outlined
                          dense
                          hide-details
                          class="nexus-input"
                          :disabled="!isEditing"
                        ></v-text-field>
                      </v-col>

                      <!-- Description -->
                      <v-col cols="6" md="3">
                        <div class="detail-label">Description</div>
                        <v-text-field
                          v-model="returnItem.description"
                          :rules="[rules.required]"
                          outlined
                          dense
                          hide-details
                          class="nexus-input"
                          :disabled="!isEditing"
                        ></v-text-field>
                      </v-col>

                      <!-- Reference Number -->
                      <v-col cols="6" md="3">
                        <div class="detail-label">Reference Number</div>
                        <v-text-field
                          v-model="returnItem.refnum"
                          :rules="[rules.required]"
                          outlined
                          dense
                          hide-details
                          class="nexus-input"
                          :disabled="!isEditing"
                        ></v-text-field>
                      </v-col>

                      <!-- Status -->
                      <v-col cols="6" md="2">
                        <div class="detail-label">Status</div>
                        <v-select
                          v-model="returnItem.status"
                          :items="['Approved', 'Rejected']"
                          outlined
                          dense
                          hide-details
                          class="nexus-input"
                          :disabled="!isEditing"
                        ></v-select>
                      </v-col>
                    </v-row>

                    <!-- Rejection Details Box (only for rejected items) -->
                    <div v-if="returnItem.status === 'Rejected'" class="rejection-box mt-3">
                      <v-row>
                        <!-- Rejected Quantity -->
                        <v-col cols="6" md="6">
                          <div class="detail-label">
                            <v-icon small color="#991b1b">mdi-alert</v-icon>
                            Rejected Quantity
                          </div>
                          <v-text-field
                            v-model="returnItem.rejectedQty"
                            :rules="[rules.numeric]"
                            outlined
                            dense
                            hide-details
                            class="nexus-input mt-2"
                            :disabled="!isEditing"
                          ></v-text-field>
                        </v-col>

                        <!-- Rejection Reason -->
                        <v-col cols="12" md="6">
                          <div class="detail-label">
                            <v-icon small color="#991b1b">mdi-comment-alert</v-icon>
                            Rejection Reason
                          </div>
                          <v-text-field
                            v-model="returnItem.rejectionReason"
                            :rules="[rules.required]"
                            outlined
                            dense
                            hide-details
                            class="nexus-input mt-2"
                            :disabled="!isEditing"
                          ></v-text-field>
                        </v-col>
                      </v-row>
                    </div>

                    <!-- Photo Actions -->
                    <div class="action-buttons mt-4">
                      <v-btn
                        class="nexus-btn mr-2"
                        @click="openPhotoCarousel(returnItem)"
                        small
                      >
                        <v-icon class="mr-1" size="small">mdi-eye</v-icon>
                        View Photos
                      </v-btn>
                      <v-btn
                        class="nexus-btn mr-2"
                        @click="downloadPhotos(returnItem)"
                        small
                      >
                        <v-icon class="mr-1" size="small">mdi-download</v-icon>
                        Download
                      </v-btn>
                      <v-btn
                        class="nexus-btn"
                        @click="triggerFileInput(index)"
                        small
                      >
                        <v-icon class="mr-1" size="small">mdi-camera</v-icon>
                        Upload New
                      </v-btn>
                      <!-- Hidden File Input -->
                      <input
                        :ref="'fileInput' + index"
                        type="file"
                        accept="image/*"
                        style="display:none"
                        multiple
                        @change="handleFileUpload($event, index)"
                      />
                    </div>
                  </div>

                  <!-- Bottom Actions -->
                  <div class="bottom-actions mt-6">
                    <v-btn
                      class="nexus-btn-secondary"
                      @click="confirmAddItem"
                      :disabled="!isEditing"
                    >
                      <v-icon class="mr-2">mdi-plus-circle-outline</v-icon>
                      Add More Return Items
                    </v-btn>
                    <v-btn
                      large
                      class="nexus-btn-save"
                      @click="confirmSubmit"
                    >
                      <v-icon class="mr-2">mdi-content-save</v-icon>
                      Save Changes
                    </v-btn>
                  </div>
                </div>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Photo Carousel Dialog -->
      <v-dialog v-model="photoDialog.visible" max-width="800px" persistent>
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            Photos
            <v-btn icon variant="text" @click="photoDialog.visible = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-0">
            <v-carousel 
              v-if="photoDialog.photos.length > 0" 
              height="500"
              hide-delimiter-background
            >
              <v-carousel-item v-for="(photo, index) in photoDialog.photos" :key="index">
                <v-img :src="photo" height="500" contain></v-img>
              </v-carousel-item>
            </v-carousel>
            <div v-else class="text-center pa-8">
              No photos available
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn variant="flat" color="primary" @click="photoDialog.visible = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Confirmation Dialog -->
      <v-dialog v-model="confirmationDialog.visible" max-width="400px">
        <v-card>
          <v-card-title class="headline">{{ confirmationDialog.title }}</v-card-title>
          <v-card-text>{{ confirmationDialog.message }}</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="green-darken-1" variant="text" @click="confirmationDialog.confirm">
              Yes
            </v-btn>
            <v-btn color="red-darken-1" variant="text" @click="confirmationDialog.visible = false">
              No
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
} from 'firebase/firestore';
import { getStorage, ref, getDownloadURL, uploadBytes } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { firebaseApp, db } from '../../firebaseConfig';
import DisplayChecklist from '@/components/DisplayChecklist.vue';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

const storage = getStorage(firebaseApp);

export default {
  components: {
    DisplayChecklist,
  },
  data() {
    return {
      valid: false,
      photoDialog: {
        visible: false,
        photos: [],
      },
      confirmationDialog: {
        visible: false,
        title: '',
        message: '',
        confirm: null,
      },
      returnData: {
        id: '',
        returnId: '',
        returnStatus: '',
        receivedBy: null,
        inspection: null,
        assignedTo: null,
        contactPerson: '',
        invoiceNumber: '',
        creditNoteNumber: '',
        returnReason: '',
        returnItems: [],
        previous_values: [],
        deliveryDate: '',
        createdDate: '',
      },
      originalValues: {},
      isInitialLoad: true,
      customers: [],
      selectedCustomer: null,
      users: [],
      // Updated Received by options with new names
      receivedByOptions: [
        "Kate Mpofu",
        "Thabo Mohlatlole",
        "Patricia Mzolo",
        "Tumi Makgotlwe",
        "Precious Mahlaba"
      ],
      assignedToOptions: [],
      loading: false,
      // Updated status options - simple strings
      statusOptions: [
        "Booked",
        "Inspection",
        "Pending Credit",
        "Labels and Packaging",
        "Return to Stores",
        "Completed",
        "Rejected",
      ],
      deliveryDate: '',
      deliveryDateMenu: false,
      createdDate: '',
      // currentUser will hold the current user's document from Firestore
      currentUser: null,
      // Define validation rules
      rules: {
        required: (value) => !!value || 'This field is required',
        numeric: (value) => !isNaN(value) || 'Must be numeric',
        nonNegative: (value) => value >= 0 || 'Cannot be negative',
      },
    };
  },
  async created() {
    this.loading = true;
    // Fetch current user first
    await this.fetchCurrentUser();
    await this.fetchUsers();
    await this.fetchReturnData();
    await this.fetchCustomers();
    this.loading = false;
  },
  computed: {
    // isEditing is true only when returnStatus is 'Inspection'
    isEditing() {
      return this.returnData.returnStatus === 'Inspection';
    },
    canEditReturnStatus() {
      // Use currentUser's role if available; fallback to 'guest'
      const role = this.currentUser ? this.currentUser.role : 'guest';
      return role === 'technician' || role === 'salesperson' || role === 'Admin';
    },
    creditNoteNumberRules() {
      if (this.returnData.returnStatus === 'Labels and Packaging') {
        return [this.rules.required];
      }
      return [];
    },
    hasPhotos() {
      return this.returnData.returnItems.some(
        (item) => item.pictures && item.pictures.length > 0
      );
    },
    // Computed property that hides return line items if the current user's
    // returns permission "View_Line_Items_(Labour_and_Stock)" is true.
    hideReturnLineItems() {
      return (
        this.currentUser &&
        this.currentUser.returns &&
        this.currentUser.returns["View_Line_Items_(Labour_and_Stock)"] === true
      );
    },
  },
  watch: {
    selectedCustomer(newVal) {
      if (!this.isInitialLoad && newVal) {
        if (!this.returnData.contactPerson) {
          this.returnData.contactPerson = newVal.contactPerson;
        }
      }
    },
    'returnData.returnStatus'(newVal, oldVal) {
      this.fetchAssignedToOptions();
      this.storePreviousValue('returnStatus', oldVal, newVal);
    },
    'returnData.receivedBy'(newVal, oldVal) {
      this.storePreviousValue('receivedBy', oldVal, newVal);
    },
    'returnData.inspection'(newVal, oldVal) {
      this.storePreviousValue('assignedTo', oldVal, newVal);
    },
  },
  methods: {
    async fetchCurrentUser() {
      const auth = getAuth(firebaseApp);
      const user = auth.currentUser;
      if (user) {
        try {
          const userRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            this.currentUser = userDoc.data();
          }
        } catch (error) {
          console.error('Error fetching current user data:', error);
        }
      }
    },
    async fetchUsers() {
      try {
        const usersCollection = collection(db, 'users');
        const usersSnapshot = await getDocs(usersCollection);
        this.users = usersSnapshot.docs.map((doc) => ({
          docId: doc.id,
          ...doc.data(),
        }));
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    },
    formatDate(date) {
      if (!date) return 'N/A';
      
      // Handle Firestore Timestamp
      if (date && typeof date.toDate === 'function') {
        date = date.toDate();
      }
      
      // Handle string dates
      if (typeof date === 'string') {
        date = new Date(date);
      }
      
      // Check if valid date
      if (!(date instanceof Date) || isNaN(date)) {
        return 'N/A';
      }
      
      // Format date nicely
      const options = { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      };
      
      return date.toLocaleString('en-US', options);
    },
    async fetchReturnData() {
      try {
        // Try multiple ways to get the return ID
        let returnId = this.$route.query?.id || this.$route.params?.id || null;
        
        // Fallback: parse from URL directly
        if (!returnId) {
          const urlParams = new URLSearchParams(window.location.search);
          returnId = urlParams.get('id');
        }
        
        // Another fallback: check if ID is in the path
        if (!returnId) {
          const pathSegments = window.location.pathname.split('/');
          const editIndex = pathSegments.indexOf('EditReturns');
          if (editIndex !== -1 && pathSegments[editIndex + 1]) {
            returnId = pathSegments[editIndex + 1];
          }
        }
        
        if (!returnId || returnId === 'undefined' || returnId === 'null') {
          console.error('No return ID provided in route. Route params:', this.$route.params, 'Route query:', this.$route.query, 'URL:', window.location.href);
          this.loading = false;
          return;
        }
        
        const returnRef = doc(db, 'returns', String(returnId));
        const returnSnapshot = await getDoc(returnRef);
        if (returnSnapshot.exists()) {
          const data = returnSnapshot.data();
          this.returnData = data;
          this.returnData.id = returnId;
          this.returnData.creditNoteNumber = data.creditNoteNumber || '';
          if (!data.previous_values) {
            this.returnData.previous_values = [];
          } else {
            this.returnData.previous_values = data.previous_values;
          }
          this.originalValues = {
            receivedBy: data.receivedBy,
            returnStatus: data.returnStatus,
            inspection: data.inspection,
          };
          this.deliveryDate = data.deliveryDate || data.timestamp || new Date().toLocaleString();
          this.createdDate = data.createdDate || data.timestamp || new Date().toLocaleString();
          if (data.customer && data.customer.docId) {
            const customer = await this.findCustomerById(data.customer.docId);
            if (customer) {
              this.selectedCustomer = customer;
            } else {
              this.customers.push(data.customer);
              this.selectedCustomer = data.customer;
            }
          }
        if (!data.returnItems) {
          this.$set(this.returnData, 'returnItems', []);
        }
        // Handle assignedTo - extract the name string for the dropdown
        if (data.assignedTo && data.assignedTo.name) {
          this.returnData.inspection = data.assignedTo.name;
        } else if (data.inspection) {
          this.returnData.inspection = data.inspection;
        }
        this.fetchAssignedToOptions();
      } else {
        console.error('No such document!');
      }
      this.isInitialLoad = false;
    } catch (error) {
      console.error('Error fetching return data:', error);
      this.isInitialLoad = false;
    }
    },
    async fetchCustomers() {
      try {
        const customersCollection = collection(db, 'customers');
        const customersSnapshot = await getDocs(customersCollection);
        this.customers = customersSnapshot.docs
          .map((doc) => ({
            docId: doc.id,
            companyName: doc.data()["Company Name"] || "",
            contactPerson: doc.data()["Contact Person"] || "",
            customerAccount: doc.data()["Customer Account"] || "",
          }))
          .sort((a, b) => a.companyName.localeCompare(b.companyName));
      } catch (error) {
        console.error("Error fetching customers:", error);
      }
    },
    async findCustomerById(docId) {
      if (this.customers.length === 0) {
        await this.fetchCustomers();
      }
      return this.customers.find((customer) => customer.docId === docId);
    },
    // Use a hard-coded mapping for Assigned To options - simple strings
    fetchAssignedToOptions() {
      const mapping = {
        Booked: [
          "Cameron Johnston",
          "Carla-Jo Barry",
          "Rodney Theunissen",
        ],
        Inspection: [
          "Cameron Johnston",
          "Colin Tee",
          "Fred Norminton",
          "Jason Steyn",
          "Rodney Theunissen",
          "Tawanda Nkomo",
          "Thabo B Bofelo",
          "Tori Peters",
          "Tumelo Diphoko",
        ],
        "Pending Credit": [
          "Betty Gumede",
          "Cameron Johnston",
          "Carla-Jo Barry",
          "Rodney Theunissen",
        ],
        "Labels and Packaging": [
          "Kate Mpofu",
          "Precious Mahlaba",
        ],
        "Return to Stores": [
          "Jason Steyn",
          "Tumelo Diphoko",
        ],
      };

      if (mapping[this.returnData.returnStatus]) {
        this.assignedToOptions = mapping[this.returnData.returnStatus].sort();
      } else {
        this.assignedToOptions = [];
      }
    },
    storePreviousValue(fieldName, oldVal, newVal) {
      if (oldVal && newVal !== oldVal) {
        const formattedFieldName =
          fieldName === 'returnStatus' ? 'Return Status' :
          fieldName === 'inspection' ? 'Assigned To' : fieldName;
        let lastEntry = this.returnData.previous_values[this.returnData.previous_values.length - 1];
        if (!lastEntry || new Date() - new Date(lastEntry.dateTime) > 10000) {
          lastEntry = { dateTime: new Date().toLocaleString() };
          this.returnData.previous_values.push(lastEntry);
        }
        this.$set(lastEntry, formattedFieldName, oldVal);
      }
    },
    confirmAddItem() {
      this.confirmationDialog = {
        visible: true,
        title: 'Confirm Action',
        message: 'Are you sure you want to add a new return item?',
        confirm: this.addMoreReturnItems,
      };
    },
    addMoreReturnItems() {
      this.confirmationDialog.visible = false;
      if (!this.returnData.returnItems) {
        this.$set(this.returnData, 'returnItems', []);
      }
      this.returnData.returnItems.push({
        qty: '',
        code: '',
        description: '',
        refnum: '',
        status: 'Approved',
        rejectedQty: '',
        rejectionReason: '',
        pictures: [],
      });
    },
    confirmRemoveItem(index) {
      this.confirmationDialog = {
        visible: true,
        title: 'Confirm Deletion',
        message: 'Are you sure you want to remove this return item?',
        confirm: () => this.removeReturnItem(index),
      };
    },
    removeReturnItem(index) {
      this.confirmationDialog.visible = false;
      this.returnData.returnItems.splice(index, 1);
    },
    confirmSubmit() {
      this.confirmationDialog = {
        visible: true,
        title: 'Confirm Save',
        message: 'Are you sure you want to save changes?',
        confirm: this.submit,
      };
    },
    async submit() {
      if (this.$refs.form.validate()) {
        this.loading = true;
        try {
          // Build assignedTo object from the selected name string
          const assignedToUser = this.returnData.inspection ? {
            name: this.returnData.inspection,
            uid: this.returnData.inspection,
          } : null;
          
          const returnItemsData = this.returnData.returnItems.map((item) => ({
            qty: item.qty,
            code: item.code,
            description: item.description,
            refnum: item.refnum,
            status: item.status,
            rejectedQty: item.status === 'Rejected' ? item.rejectedQty : null,
            rejectionReason: item.status === 'Rejected' ? item.rejectionReason : null,
            pictures: item.pictures || [],
          }));
          if (!this.selectedCustomer) {
            console.error("Customer information is missing.");
            this.loading = false;
            return;
          }
          const updatedReturnData = {
            returnId: this.returnData.returnId,
            returnStatus: this.returnData.returnStatus,
            receivedBy: this.returnData.receivedBy,
            inspection: this.returnData.inspection,
            assignedTo: assignedToUser,
            customer: {
              docId: this.selectedCustomer.docId,
              companyName: this.selectedCustomer.companyName,
              contactPerson: this.selectedCustomer.contactPerson,
              customerAccount: this.selectedCustomer.customerAccount,
            },
            contactPerson: this.returnData.contactPerson,
            invoiceNumber: this.returnData.invoiceNumber,
            returnReason: this.returnData.returnReason,
            returnItems: returnItemsData,
            previous_values: this.returnData.previous_values,
            deliveryDate: this.deliveryDate,
            createdDate: this.createdDate,
            timestamp: new Date().toLocaleString(),
          };
          // Always save the credit note number if it exists
if (this.returnData.creditNoteNumber) {
  updatedReturnData.creditNoteNumber = this.returnData.creditNoteNumber;
}

// Validate it's required for Labels and Packaging
if (this.returnData.returnStatus === 'Labels and Packaging' && !this.returnData.creditNoteNumber) {
  throw new Error("Credit Note Number is required for Labels and Packaging status.");
}
          await updateDoc(doc(db, 'returns', this.returnData.id), updatedReturnData);
          this.$router.go(-1);
        } catch (error) {
          console.error("Error updating return data:", error);
          if (this.$toast) {
            this.$toast.error("Failed to update return data. Please try again.");
          } else {
            alert("Failed to update return data. Please try again.");
          }
        } finally {
          this.loading = false;
        }
      }
    },
    confirmBackNavigation() {
      this.confirmationDialog = {
        visible: true,
        title: 'Confirm Navigation',
        message: 'Are you sure you want to go back? Unsaved changes will be lost.',
        confirm: this.goBack,
      };
    },
    goBack() {
      this.confirmationDialog.visible = false;
      this.$router.go(-1);
    },
    async openPhotoCarousel(returnItem) {
      if (returnItem.pictures && returnItem.pictures.length > 0) {
        const promises = returnItem.pictures.map(async (picture) => {
          try {
            const storageRef = ref(storage, picture);
            const downloadURL = await getDownloadURL(storageRef);
            return downloadURL;
          } catch (error) {
            console.error(`Error fetching download URL for ${picture}:`, error);
            return null;
          }
        });
        try {
          const urls = await Promise.all(promises);
          this.photoDialog.photos = urls.filter(url => url);
          this.photoDialog.visible = true;
        } catch (error) {
          console.error('Error fetching photo URLs:', error);
        }
      } else {
        this.photoDialog.photos = [];
        this.photoDialog.visible = true;
      }
    },
    async downloadPhotos(returnItem) {
      if (!returnItem.pictures || returnItem.pictures.length === 0) {
        alert('No photos available for download.');
        return;
      }
      const zip = new JSZip();
      const promises = returnItem.pictures.map(async (picture) => {
        try {
          const storageRef = ref(storage, picture);
          const url = await getDownloadURL(storageRef);
          const response = await fetch(url);
          const blob = await response.blob();
          const fileName = picture.split('/').pop();
          zip.file(fileName, blob);
        } catch (error) {
          console.error('Error downloading photo:', error);
        }
      });
      await Promise.all(promises);
      try {
        const content = await zip.generateAsync({ type: 'blob' });
        saveAs(content, 'photos.zip');
      } catch (error) {
        console.error('Error generating zip file:', error);
      }
    },
    async downloadAllPhotos() {
      const allPictures = [];
      this.returnData.returnItems.forEach((item, index) => {
        if (item.pictures && item.pictures.length > 0) {
          item.pictures.forEach((pic) => {
            allPictures.push({ path: pic, itemIndex: index });
          });
        }
      });
      if (allPictures.length === 0) {
        alert('No photos available to download.');
        return;
      }
      const zip = new JSZip();
      const folder = zip.folder('All_Return_Item_Photos') || zip;
      const promises = allPictures.map(async (pictureObj) => {
        const { path, itemIndex } = pictureObj;
        try {
          const storageRef = ref(storage, path);
          const url = await getDownloadURL(storageRef);
          const response = await fetch(url);
          const blob = await response.blob();
          const fileName = `ReturnItem_${itemIndex + 1}_${path.split('/').pop()}`;
          folder.file(fileName, blob);
        } catch (error) {
          console.error(`Error downloading photo ${path}:`, error);
        }
      });
      try {
        await Promise.all(promises);
        const content = await zip.generateAsync({ type: 'blob' });
        saveAs(content, 'all_photos.zip');
      } catch (error) {
        console.error('Error generating zip file:', error);
        alert('Failed to download all photos. Please try again.');
      }
    },
    triggerFileInput(index) {
      if (this.$refs[`fileInput${index}`] && this.$refs[`fileInput${index}`][0]) {
        this.$refs[`fileInput${index}`][0].click();
      }
    },
    async handleFileUpload(event, index) {
      const files = event.target.files;
      if (files && files.length > 0) {
        for (let file of files) {
          const timestamp = Date.now();
          const storagePath = `returns/${this.returnData.returnId || this.returnData.id}/${timestamp}_${file.name}`;
          const storageRef = ref(storage, storagePath);
          try {
            await uploadBytes(storageRef, file);
            if (!this.returnData.returnItems[index].pictures) {
              this.$set(this.returnData.returnItems[index], 'pictures', []);
            }
            this.returnData.returnItems[index].pictures.push(storagePath);
          } catch (error) {
            console.error('Error uploading photo:', error);
          }
        }
      }
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap");

.nexus-container {
  background: linear-gradient(135deg, #475569 0%, #334155 100%);
  min-height: 100vh;
  font-family: 'Inter', sans-serif;
}

.nexus-title {
  color: white;
  font-weight: 700;
  font-size: 2.5rem;
  text-align: center;
  letter-spacing: 2px;
  margin: 0;
}

.nexus-card {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 
    0 20px 40px -12px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  margin-top: 20px;
  transition: all 0.3s ease;
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
}

.section-title {
  color: #475569;
  font-weight: 600;
  font-size: 1.25rem;
  letter-spacing: 1px;
  margin: 0;
  white-space: nowrap;
}

.section-divider {
  flex: 1;
  height: 2px;
  background: linear-gradient(90deg, #475569 0%, rgba(71, 85, 105, 0.2) 100%);
  margin-left: 20px;
  border-radius: 1px;
}

.nexus-input >>> .v-input__control {
  border-radius: 12px;
}

.nexus-input >>> .v-input__slot {
  background: rgba(248, 250, 252, 0.8) !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  transition: all 0.3s ease;
}

.nexus-input >>> .v-input__slot:hover {
  border-color: #475569 !important;
  background: rgba(255, 255, 255, 0.9) !important;
}

.nexus-input >>> .v-input--is-focused .v-input__slot {
  border-color: #475569 !important;
  background: rgba(255, 255, 255, 1) !important;
  box-shadow: 0 0 0 3px rgba(71, 85, 105, 0.15);
}

.nexus-input >>> .v-input--is-disabled .v-input__slot {
  background: rgba(226, 232, 240, 0.5) !important;
  border-color: #cbd5e1 !important;
  opacity: 1 !important;
}

.nexus-input >>> .v-input--is-disabled input,
.nexus-input >>> .v-input--is-disabled textarea,
.nexus-input >>> .v-input--is-disabled .v-select__selection {
  color: #475569 !important;
  -webkit-text-fill-color: #475569 !important;
  opacity: 1 !important;
}

.nexus-input >>> .v-input--is-disabled .v-label,
.nexus-input >>> .v-input--is-disabled .v-icon {
  color: #64748b !important;
  opacity: 1 !important;
}

.nexus-btn {
  background: linear-gradient(135deg, #475569 0%, #334155 100%) !important;
  color: white !important;
  border-radius: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  text-transform: none !important;
  box-shadow: 0 4px 14px 0 rgba(71, 85, 105, 0.3) !important;
  transition: all 0.3s ease !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.nexus-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 25px 0 rgba(71, 85, 105, 0.4) !important;
}

.back-button {
  color: white !important;
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 50% !important;
  border: 1px solid rgba(255, 255, 255, 0.3);
  position: absolute;
  top: 80px;
  left: 24px;
  transition: all 0.3s ease !important;
  box-shadow: 0 4px 14px 0 rgba(71, 85, 105, 0.4) !important;
}

.back-button:hover {
  background: rgba(255, 255, 255, 0.3) !important;
  transform: scale(1.1) !important;
  box-shadow: 0 8px 25px 0 rgba(71, 85, 105, 0.5) !important;
}

.back-button .v-icon {
  color: white !important;
}

.photo {
  width: 100%;
  height: auto;
  border-radius: 20px;
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #475569 0%, #334155 100%);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #334155 0%, #1e293b 100%);
}

/* Form validation styles */
.nexus-input >>> .v-messages__message {
  color: #dc2626 !important;
  font-weight: 500 !important;
}

.nexus-input >>> .v-input--has-state .v-input__slot {
  border-color: #dc2626 !important;
}

/* Loading overlay */
.v-overlay >>> .v-overlay__content {
  background: rgba(15, 23, 42, 0.8) !important;
  backdrop-filter: blur(4px) !important;
  border-radius: 16px !important;
  padding: 30px !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  .nexus-title {
    font-size: 1.75rem;
    letter-spacing: 1px;
  }
  
  .section-title {
    font-size: 1.1rem;
  }
  
  .nexus-card {
    margin: 10px;
    border-radius: 16px;
  }
  
  .nexus-card >>> .v-card__text {
    padding: 20px !important;
  }
}

/* ========================================
   REDESIGNED LAYOUT STYLES
   ======================================== */

/* Section Headers */
.redesign-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e2e8f0;
}

.redesign-section-title {
  color: #1e293b;
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 0.5px;
}

/* Info Boxes (Read-only fields) */
.info-box {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  border-radius: 10px;
  padding: 14px;
  border-left: 3px solid #475569;
}

.info-item {
  margin-bottom: 12px;
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #475569;
  font-size: 0.75rem;
  font-weight: 500;
  margin-bottom: 3px;
}

.info-value {
  color: #1e293b;
  font-size: 0.875rem;
  font-weight: 600;
  padding-left: 22px;
}

/* Editable Field Groups */
.field-group {
  background: white;
  border-radius: 10px;
  padding: 14px;
  border: 2px solid #e2e8f0;
}

.field-label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #475569;
  font-size: 0.75rem;
  font-weight: 500;
  margin-bottom: 0;
}

/* Return Reason Box */
.reason-box {
  background: #f8fafc;
  border-radius: 10px;
  padding: 14px;
  border: 2px solid #e2e8f0;
}

.reason-label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #475569;
  font-size: 0.75rem;
  font-weight: 500;
  margin-bottom: 0;
}

/* Item Cards */
.item-card {
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 12px;
  transition: all 0.3s;
}

.item-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: #cbd5e1;
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
}

.item-number {
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 500;
}

/* Status Badges */
.status-approved {
  background: #dcfce7 !important;
  color: #166534 !important;
  border: none !important;
}

.status-rejected {
  background: #fee2e2 !important;
  color: #991b1b !important;
  border: none !important;
}

/* Item Details */
.item-details {
  margin-bottom: 0;
}

.detail-label {
  color: #64748b;
  font-size: 0.7rem;
  font-weight: 500;
  margin-bottom: 6px;
}

.detail-value {
  color: #1e293b;
  font-size: 0.8125rem;
  font-weight: 600;
}

/* Rejection Box */
.rejection-box {
  background: #fef2f2;
  border: 2px solid #fecaca;
  border-radius: 8px;
  padding: 12px;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.nexus-btn-secondary {
  background: linear-gradient(135deg, #64748b 0%, #475569 100%) !important;
  color: white !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  text-transform: none !important;
  padding: 12px 24px !important;
  box-shadow: 0 2px 8px rgba(100, 116, 139, 0.3) !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.nexus-btn-secondary:hover {
  background: linear-gradient(135deg, #475569 0%, #334155 100%) !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(100, 116, 139, 0.4) !important;
}

.nexus-btn-save {
  background: linear-gradient(135deg, #475569 0%, #334155 100%) !important;
  color: white !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  text-transform: none !important;
  font-size: 1rem !important;
  padding: 14px 32px !important;
  box-shadow: 0 4px 12px rgba(71, 85, 105, 0.3) !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.nexus-btn-save:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(71, 85, 105, 0.4) !important;
}

/* Bottom Actions */
.bottom-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

/* Responsive Design for Redesign */
@media (max-width: 768px) {
  .redesign-section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .item-details .v-col {
    flex: 0 0 100%;
    max-width: 100%;
  }

  .bottom-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .bottom-actions .v-btn {
    width: 100%;
  }
}

/* Hide validation messages in item cards for cleaner look */
.item-card .v-text-field--outlined >>> .v-text-field__details {
  display: none !important;
}

.item-card .v-select--outlined >>> .v-text-field__details {
  display: none !important;
}
</style>